import { useState, useEffect } from "react";
import { Box, Alert, CircularProgress, Button, Divider } from "@mui/material";

import { FieldControl } from "../../../shared/components";
import { useDisplayField } from "../../../hooks/useDisplayField";
import { useCreateMaterialRequest, useGetActivity, useUpdateMaterialRequest } from "../mutations";
import { usePhase } from "../../projectOverview/hooks/usePhase";
import { useGetMaterialReqDetails } from "../hooks/useGetMaterialReqDetails";
import { useStakeHolder } from "../../projectOverview/hooks/useStakeHolder";

interface AddRequestProps {
    projectId: string | undefined;
    requestId: string | null;
    isEdit: boolean;
    onSuccess: () => void;
    onClose: () => void;
    refetch: () => void;
}

export const AddRequest = ({ projectId, requestId, isEdit = false, onSuccess, onClose, refetch }: AddRequestProps) => {

    const { getFilterOptions } = useDisplayField([]);

    const phaseData = usePhase();
    const activityData = useGetActivity();

    const createMutation = useCreateMaterialRequest();
    const updateMutation = useUpdateMaterialRequest();

    const [requestData, setRequestData] = useState({
        type: '',
        description: '',
        quantity: 0,
        required_date: '',
        activity_id: '',
        supplier: '',
        point_of_contact_info: '',
        owner_of_request: '',
        status: '',
        last_updated_comments: '',
        last_updated_date: '',
        order_placed_date: '',
        est_delivery_date: '',
        phase_id: '',
    });

    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleChange = (key: string, value: any) => {
        setRequestData((prevData) => ({
            ...prevData,
            [key]: value,
        }));
        if (error) setError(null);
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            setError(null);
            const payload = {
                "project_id": projectId,
                "phase_id": requestData.phase_id,
                "activity_id": requestData.activity_id,
                "type": requestData.type,
                "description": requestData.description,
                "quantity": requestData.quantity,
                "order_placed_date": requestData.order_placed_date,
                "est_delivery_date": requestData.est_delivery_date,
                "supplier": requestData.supplier,
                "point_of_contact_info": requestData.point_of_contact_info,
                "owner_of_request": requestData.owner_of_request,
                "status": requestData.status,
                "required_date": requestData.required_date,
                "last_updated_comments": requestData.last_updated_comments,
                "last_updated_date": requestData.last_updated_date,
            };
            if (isEdit && requestId) {
                await updateMutation.mutateAsync({ id: requestId, payload });
            } else {
                await createMutation.mutateAsync({ payload });
            }
            refetch?.();
            onSuccess?.();
            onClose?.();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : `Failed to ${isEdit ? 'update' : 'create'} material request`;
            setError(errorMessage);
            console.error(`Failed to ${isEdit ? 'update' : 'create'} material request:`, err);
        } finally {
            setLoading(false);
        }
    };

    const isFormValid = requestData.type && requestData.description && requestData.quantity > 0;

    const {
        data,
        isFetching: isMaterialReqFetching, isPending: isMaterialReqPending, isError: isMaterialReqError
    } = useGetMaterialReqDetails({ projectId, requestId });

    const {
        stakeHolderData,
        isPending: isStakeHolderPending, isFetching: isStakeHolderFetching, isError: isStakeHolderError,
    } = useStakeHolder({ projectId, isRemovePagination: true });

    useEffect(() => {
        if (requestData.phase_id) {
            activityData.mutateAsync({ projectId: projectId, phaseId: requestData.phase_id });
        }
    }, [requestData.phase_id]);

    useEffect(() => {
        phaseData.mutateAsync(projectId);
    }, []);

    useEffect(() => {
        if (isEdit && requestId && data && !isMaterialReqFetching && !isMaterialReqPending && !isMaterialReqError) {
            setRequestData({
                type: data.type || '',
                description: data.description || '',
                quantity: data.quantity || 0,
                required_date: data.required_date || '',
                activity_id: data.activity_id || '',
                supplier: data.supplier || '',
                point_of_contact_info: data.point_of_contact_info || '',
                owner_of_request: data.owner_of_request || '',
                status: data.status || '',
                last_updated_comments: data.last_updated_comments || '',
                last_updated_date: data.last_updated_date || '',
                order_placed_date: data.order_placed_date || '',
                est_delivery_date: data.est_delivery_date || '',
                phase_id: data.phase_id || '',
            });
        }
    }, [isEdit, requestId, data, isMaterialReqFetching, isMaterialReqPending, isMaterialReqError]);

    if (isStakeHolderPending || isStakeHolderFetching) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>

            {error && (
                <Box sx={{ px: 2, pt: 2 }}>
                    <Alert severity="error" onClose={() => setError(null)}>
                        {error}
                    </Alert>
                </Box>
            )}

            <Box sx={{ px: 2, py: 2, flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>

                <FieldControl
                    keyName={"type"}
                    label={"Type"}
                    variant={"select"}
                    value={requestData.type}
                    options={getFilterOptions("MATERIAL_TYPE").map((option) => ({ label: option.display_text, value: option.key }))}
                    onChange={(_key: string, value: string) => handleChange("type", value)}
                    disabled={loading}
                />

                <FieldControl
                    keyName={"description"}
                    label={"Description"}
                    variant={"input"}
                    value={requestData.description}
                    onChange={(_key: string, value: string) => handleChange("description", value)}
                    disabled={loading}
                />

                <FieldControl
                    keyName={"quantity"}
                    label={"Quantity"}
                    variant={"input"}
                    value={requestData.quantity.toString()}
                    onChange={(_key: string, value: string) => handleChange("quantity", parseInt(value) || 0)}
                    disabled={loading}
                />

                <FieldControl
                    keyName={"required_date"}
                    label={"Required Date"}
                    variant={"date"}
                    value={requestData.required_date}
                    onChange={(_key: string, value: string) => handleChange("required_date", value)}
                    disabled={loading}
                />

                <FieldControl
                    keyName={"est_delivery_date"}
                    label={"Estimated Delivery Date"}
                    variant={"date"}
                    value={requestData.est_delivery_date}
                    onChange={(_key: string, value: string) => handleChange("est_delivery_date", value)}
                    disabled={loading}
                />

                <FieldControl
                    keyName={"supplier"}
                    label={"Supplier"}
                    variant={"select"}
                    value={requestData.supplier}
                    options={requestData.type ? getFilterOptions("MATERIAL_SUPPLIER").filter((m) => { return m.parent_key?.split(',').map((s: string) => s.trim()).includes(requestData.type) }).map((option) => ({ label: option.display_text, value: option.key })) : []}
                    onChange={(_key: string, value: string) => handleChange("supplier", value)}
                    disabled={loading}
                />

                <FieldControl
                    keyName={"phase_id"}
                    label={"Phase"}
                    variant={"select"}
                    value={requestData.phase_id}
                    options={phaseData.data?.data.map((phase: { id: string; name: string }) => ({ label: phase.name, value: phase.id })) || []}
                    onChange={(_key: string, value: string) => handleChange("phase_id", value)}
                    disabled={loading}
                />

                <FieldControl
                    keyName={"activity_id"}
                    label={"Activity"}
                    variant={"select"}
                    value={requestData.activity_id}
                    options={activityData.data?.data.rows.map((activity: { id: string; name: string }) => ({ label: activity.name, value: activity.id })) || []}
                    onChange={(_key: string, value: string) => handleChange("activity_id", value)}
                    disabled={loading}
                />

                <FieldControl
                    keyName={"status"}
                    label={"Status"}
                    variant={"select"}
                    value={requestData.status}
                    options={getFilterOptions("MATERIAL_STATUS").map((option) => ({ label: option.display_text, value: option.key }))}
                    onChange={(_key: string, value: string) => handleChange("status", value)}
                    disabled={loading}
                />

                <FieldControl
                    keyName={"order_placed_date"}
                    label={"Date Order Placed"}
                    variant={"date"}
                    value={requestData.order_placed_date}
                    onChange={(_key: string, value: string) => handleChange("order_placed_date", value)}
                    disabled={loading}
                />

                <FieldControl
                    keyName={"point_of_contact_info"}
                    label={"Point of Contact Info"}
                    variant={"input"}
                    value={requestData.point_of_contact_info}
                    onChange={(_key: string, value: string) => handleChange("point_of_contact_info", value)}
                    disabled={loading}
                />

                <FieldControl
                    keyName={"owner_of_request"}
                    label={"Owner of Request"}
                    variant={"select"}
                    options={stakeHolderData.map((s: { derived_name: string; id: string }) => { return { label: s.derived_name, value: s.id } })}
                    value={requestData.owner_of_request}
                    onChange={(_key: string, value: string) => handleChange("owner_of_request", value)}
                    disabled={loading}
                />

                <FieldControl
                    keyName={"last_updated_comments"}
                    label={"Last Updated Comments"}
                    variant={"input"}
                    value={requestData.last_updated_comments}
                    onChange={(_key: string, value: string) => handleChange("last_updated_comments", value)}
                    disabled={loading}
                />

                <FieldControl
                    keyName={"last_updated_date"}
                    label={"Last Updated Date"}
                    variant={"date"}
                    value={requestData.last_updated_date}
                    onChange={(_key: string, value: string) => handleChange("last_updated_date", value)}
                    disabled={loading}
                />

            </Box>

            <Divider />

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    px: 3,
                    py: 2,
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || !isFormValid}
                    sx={{
                        minWidth: 150,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    {loading && <CircularProgress size={20} />}
                    {loading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Request' : 'Create Request')}
                </Button>
            </Box>

        </Box>
    );

};